document.addEventListener("DOMContentLoaded", () => {
  const VAT = {
    toggleSelector: "#vat-toggle-checkbox",
    gridSelector: ".product-grid",
    priceSelector: "price-money, .tpo_product_price, .lowest-price, .variant-price-display",
    labelSelector: ".vat-label",
    vatRate: 0.20,
    storageKey: "vat_included",
    defaultIncVat: true,
    taxableAttribute: "data-taxable",
    labels: {
      inc: "Inc VAT",
      ex: "Ex VAT",
      none: "No VAT"
    }
  };

  const toggle = document.querySelector(VAT.toggleSelector);
  if (!toggle) return;

  const formatters = {};
  let observerTimer = null;
  let isUpdating = false;

  const priceWrappers = () =>
    Array.from(document.querySelectorAll(VAT.priceSelector));

  function cleanText(value) {
    return String(value || "")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function stripVatLabels(value) {
    return cleanText(value)
      .replace(/\bInc VAT\b/gi, "")
      .replace(/\bEx VAT\b/gi, "")
      .replace(/\bNo VAT\b/gi, "")
      .replace(/\bVAT exempt\b/gi, "")
      .trim();
  }

  function cloneTextWithoutVatLabels(el) {
    const clone = el.cloneNode(true);
    clone.querySelectorAll(VAT.labelSelector).forEach(label => label.remove());
    return stripVatLabels(clone.textContent);
  }

  function toNumber(value) {
    let str = stripVatLabels(value);

    str = str
      .replace(/[^\d.,-]/g, "")
      .replace(/(?!^)-/g, "");

    if (!str) return NaN;

    const lastDot = str.lastIndexOf(".");
    const lastComma = str.lastIndexOf(",");

    if (lastDot !== -1 && lastComma !== -1) {
      if (lastDot > lastComma) {
        str = str.replace(/,/g, "");
      } else {
        str = str.replace(/\./g, "").replace(",", ".");
      }
    } else if (lastComma !== -1) {
      const decimalPlaces = str.length - lastComma - 1;

      if (decimalPlaces === 2) {
        str = str.replace(",", ".");
      } else {
        str = str.replace(/,/g, "");
      }
    }

    const number = Number(str);
    return Number.isFinite(number) ? number : NaN;
  }

  function detectCurrency(value) {
    const str = cleanText(value);

    if (str.includes("£")) return "GBP";
    if (str.includes("€")) return "EUR";
    if (str.includes("$")) return "USD";

    const match = str.match(/\b([A-Z]{3})\b/);
    return match ? match[1] : "GBP";
  }

  function getFormatter(currency) {
    const safeCurrency = currency || "GBP";

    if (!formatters[safeCurrency]) {
      const locale = safeCurrency === "EUR" ? "de-DE" : "en-GB";

      formatters[safeCurrency] = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: safeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    return formatters[safeCurrency];
  }

  function isFalseValue(value) {
    return ["false", "0", "no", "off", "not_taxable", "non-taxable"].includes(
      String(value || "").toLowerCase().trim()
    );
  }

  function isTrueValue(value) {
    return ["true", "1", "yes", "on", "taxable"].includes(
      String(value || "").toLowerCase().trim()
    );
  }

  function findTaxableSource(el) {
    if (!el || el.nodeType !== 1) return null;

    if (el.hasAttribute(VAT.taxableAttribute)) return el;

    const inner = el.querySelector(`[${VAT.taxableAttribute}]`);
    if (inner) return inner;

    const closest = el.closest(`[${VAT.taxableAttribute}]`);
    if (closest) return closest;

    return null;
  }

  function isTaxable(el) {
    const source = findTaxableSource(el);

    if (!source) return true;

    const value = source.getAttribute(VAT.taxableAttribute);

    if (isFalseValue(value)) return false;
    if (isTrueValue(value)) return true;

    return true;
  }

  function getVatLabel(el, incVat) {
    if (!isTaxable(el)) return VAT.labels.none;
    return incVat ? VAT.labels.inc : VAT.labels.ex;
  }

  function calculateValue(el, base, incVat) {
    if (!Number.isFinite(base)) return NaN;
    if (!isTaxable(el)) return base;

    return incVat ? Number((base * (1 + VAT.vatRate)).toFixed(2)) : base;
  }

  function getPriceMoneyTarget(el) {
    return el.querySelector("bdi") || el;
  }

  function setNextLabel(el, labelText) {
    let label = el.nextElementSibling;

    if (!label || !label.classList.contains("vat-label")) {
      label = document.createElement("small");
      label.className = "vat-label";
      label.setAttribute("aria-hidden", "true");
      el.insertAdjacentElement("afterend", label);
    }

    label.textContent = labelText;
  }

  function cachePriceMoney(el) {
    const bdi = getPriceMoneyTarget(el);
    if (!bdi || bdi.dataset.basePrice) return;

    const text = cloneTextWithoutVatLabels(bdi);
    const number = toNumber(text);

    if (!Number.isFinite(number)) return;

    bdi.dataset.basePrice = number;
    bdi.dataset.currency = detectCurrency(text);
  }

  function cachePlainPrice(el) {
    if (el.dataset.basePrice) return;

    const text = cloneTextWithoutVatLabels(el);
    const number = toNumber(text);

    if (!Number.isFinite(number)) return;

    el.dataset.basePrice = number;
    el.dataset.currency = detectCurrency(text);
  }

  function cacheLowestPrice(el) {
    if (el.dataset.baseMinPrice && el.dataset.baseMaxPrice) return;

    const text = cloneTextWithoutVatLabels(el);
    if (!text) return;

    const prefixMatch = text.match(/^(.*?:)\s*/);
    const prefix = prefixMatch ? prefixMatch[1] : "";
    const withoutPrefix = prefix ? text.replace(prefix, "").trim() : text;

    const cleanPriceText = stripVatLabels(withoutPrefix);
    const parts = cleanPriceText.split(/\s+to\s+/i);

    let min;
    let max;

    if (parts.length > 1) {
      min = toNumber(parts[0]);
      max = toNumber(parts[1]);
    } else {
      min = toNumber(cleanPriceText);
      max = min;
    }

    if (!Number.isFinite(min) || !Number.isFinite(max)) return;

    const span = el.querySelector("span");
    const labelStyle = span?.getAttribute("style") || "";

    el.dataset.baseMinPrice = min;
    el.dataset.baseMaxPrice = max;
    el.dataset.currency = detectCurrency(text);
    el.dataset.prefix = prefix;
    el.dataset.labelStyle = labelStyle;
  }

  function cacheMissingPrices() {
    priceWrappers().forEach(el => {
      const tag = el.tagName.toLowerCase();

      if (tag === "price-money") {
        cachePriceMoney(el);
      } else if (el.classList.contains("lowest-price")) {
        cacheLowestPrice(el);
      } else {
        cachePlainPrice(el);
      }
    });
  }

  function updatePriceMoney(el, incVat) {
    const bdi = getPriceMoneyTarget(el);
    if (!bdi) return;

    const base = parseFloat(bdi.dataset.basePrice);
    const currency = bdi.dataset.currency || "GBP";

    if (!Number.isFinite(base)) return;

    const value = calculateValue(el, base, incVat);
    const formatter = getFormatter(currency);

    bdi.textContent = formatter.format(value);
    setNextLabel(el, getVatLabel(el, incVat));
  }

  function updatePlainPrice(el, incVat) {
    const base = parseFloat(el.dataset.basePrice);
    const currency = el.dataset.currency || "GBP";

    if (!Number.isFinite(base)) return;

    const value = calculateValue(el, base, incVat);
    const formatter = getFormatter(currency);
    const label = getVatLabel(el, incVat);

    if (el.classList.contains("variant-price-display")) {
      el.innerHTML = `
        ${formatter.format(value)}
        <small class="vat-label" aria-hidden="true">${label}</small>
      `.trim();

      el.dataset.basePrice = base;
      el.dataset.currency = currency;
      return;
    }

    el.textContent = formatter.format(value);
    setNextLabel(el, label);
  }

  function updateLowestPrice(el, incVat) {
    const minBase = parseFloat(el.dataset.baseMinPrice);
    const maxBase = parseFloat(el.dataset.baseMaxPrice);

    if (!Number.isFinite(minBase) || !Number.isFinite(maxBase)) return;

    const currency = el.dataset.currency || "GBP";
    const prefix = el.dataset.prefix || "";
    const labelStyle = el.dataset.labelStyle || "";
    const formatter = getFormatter(currency);

    const min = calculateValue(el, minBase, incVat);
    const max = calculateValue(el, maxBase, incVat);
    const label = getVatLabel(el, incVat);

    const priceText =
      minBase === maxBase
        ? formatter.format(min)
        : `${formatter.format(min)} to ${formatter.format(max)}`;

    el.innerHTML = `
      ${prefix ? `${prefix} ` : ""}${priceText}
      <span style="${labelStyle}" class="vat-label" aria-hidden="true">${label}</span>
    `.trim();

    el.dataset.baseMinPrice = minBase;
    el.dataset.baseMaxPrice = maxBase;
    el.dataset.currency = currency;
    el.dataset.prefix = prefix;
    el.dataset.labelStyle = labelStyle;
  }

  function injectRangeSeparator() {
    document.querySelectorAll("price-money + price-money").forEach(second => {
      const first = second.previousElementSibling;
      if (!first) return;

      const next = first.nextSibling;

      if (
        next &&
        next.nodeType === Node.TEXT_NODE &&
        /\bto\b/i.test(next.textContent)
      ) {
        return;
      }

      first.insertAdjacentText("afterend", " to ");
    });
  }

  function updatePrices() {
    if (isUpdating) return;

    isUpdating = true;

    try {
      const incVat = toggle.checked;

      cacheMissingPrices();

      priceWrappers().forEach(el => {
        const tag = el.tagName.toLowerCase();

        if (tag === "price-money") {
          updatePriceMoney(el, incVat);
        } else if (el.classList.contains("lowest-price")) {
          updateLowestPrice(el, incVat);
        } else {
          updatePlainPrice(el, incVat);
        }
      });

      injectRangeSeparator();
      localStorage.setItem(VAT.storageKey, String(incVat));
    } finally {
      setTimeout(() => {
        isUpdating = false;
      }, 60);
    }
  }

  function scheduleUpdate(delay = 100) {
    clearTimeout(observerTimer);
    observerTimer = setTimeout(updatePrices, delay);
  }

  function schedulePredictiveSearchVatUpdate() {
    setTimeout(updatePrices, 80);
    setTimeout(updatePrices, 200);
    setTimeout(updatePrices, 450);
    setTimeout(updatePrices, 800);
  }

  const stored = localStorage.getItem(VAT.storageKey);
  toggle.checked = stored === null ? VAT.defaultIncVat : stored === "true";

  updatePrices();
  toggle.addEventListener("change", updatePrices);

  document.addEventListener("variant:change", () => scheduleUpdate(120));
  document.addEventListener("product:variant-change", () => scheduleUpdate(120));
  document.addEventListener("shopify:section:load", () => scheduleUpdate(200));

  document.addEventListener("change", event => {
    if (
      event.target.matches(
        "select[name='id'], input[name='id'], .product-variant-id"
      )
    ) {
      scheduleUpdate(120);
    }
  });

  document.addEventListener("focusin", event => {
    if (
      event.target.matches("[data-predictive-search-input], .search__input, input[type='search']") ||
      event.target.closest("predictive-search, .template-search__search, .search-modal__form")
    ) {
      schedulePredictiveSearchVatUpdate();
    }
  }, true);

  document.addEventListener("input", event => {
    if (
      event.target.matches("[data-predictive-search-input], .search__input, input[type='search']") ||
      event.target.closest("predictive-search, .template-search__search, .search-modal__form")
    ) {
      schedulePredictiveSearchVatUpdate();
    }
  }, true);

  document.addEventListener("keyup", event => {
    if (
      event.target.matches("[data-predictive-search-input], .search__input, input[type='search']") ||
      event.target.closest("predictive-search, .template-search__search, .search-modal__form")
    ) {
      schedulePredictiveSearchVatUpdate();
    }
  }, true);

  document.addEventListener("click", event => {
    if (
      event.target.closest(
        "predictive-search, .template-search__search, .search-modal__form, [data-predictive-search], [data-predictive-search-input], .predictive-search, .search__input, .search__button"
      )
    ) {
      schedulePredictiveSearchVatUpdate();
    }
  }, true);

  document.addEventListener("reset", event => {
    if (
      event.target.matches(".search-modal__form, form.search") ||
      event.target.closest("predictive-search, .template-search__search")
    ) {
      schedulePredictiveSearchVatUpdate();
    }
  }, true);

  const container = document.body;

  new MutationObserver(mutations => {
    if (isUpdating) return;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;

        if (
          node.matches?.(VAT.priceSelector) ||
          node.querySelector?.(VAT.priceSelector) ||
          node.matches?.("[data-predictive-search], .predictive-search, predictive-search") ||
          node.querySelector?.("[data-predictive-search], .predictive-search, predictive-search")
        ) {
          schedulePredictiveSearchVatUpdate();
          return;
        }
      }
    }
  }).observe(container, {
    childList: true,
    subtree: true
  });
});